/**
 * The Lumos render chain, as GLSL ES 3.0.
 *
 * The whole pipeline is scene-referred until the base pass: white balance and
 * exposure act on linear light, then a soft shoulder rolls the highlights off
 * before the sRGB encode. Everything after that works in display-referred
 * values, which is what makes the tone sliders and the curve behave the way a
 * photographer's hands already expect.
 *
 * Pass order, and why:
 *   geometry -> base -> blur pyramid -> main -> detail -> present
 *
 * Geometry runs first so the histogram, the statistics the planner reads and
 * the exported file all describe the *cropped* frame rather than the sensor.
 * Detail runs last because sharpening a frame you are about to re-tone is how
 * you get halos.
 */

export const VERTEX_SHADER = `#version 300 es
precision highp float;
out vec2 vUv;
void main() {
  // Fullscreen triangle: one draw, no vertex buffer, no seam down the middle.
  vec2 position = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  vUv = position;
  gl_Position = vec4(position * 2.0 - 1.0, 0.0, 1.0);
}`;

const COLOR_HELPERS = `
const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

float luma(vec3 c) { return dot(c, LUMA); }

float linearToSrgb(float x) {
  x = max(x, 0.0);
  return x <= 0.0031308 ? x * 12.92 : 1.055 * pow(x, 1.0 / 2.4) - 0.055;
}
vec3 linearToSrgb(vec3 c) {
  return vec3(linearToSrgb(c.r), linearToSrgb(c.g), linearToSrgb(c.b));
}

float srgbToLinear(float x) {
  x = max(x, 0.0);
  return x <= 0.04045 ? x / 12.92 : pow((x + 0.055) / 1.055, 2.4);
}
vec3 srgbToLinear(vec3 c) {
  return vec3(srgbToLinear(c.r), srgbToLinear(c.g), srgbToLinear(c.b));
}

vec3 rgbToHsv(vec3 c) {
  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
  float d = q.x - min(q.w, q.y);
  float e = 1.0e-10;
  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}

vec3 hsvToRgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}


/**
 * Folds an over-range value back under 1 instead of clamping it.
 *
 * Raising Whites stretches the range, which pushes bright pixels past 1. Simply
 * clamping there is what turns a lit patch of white fur into a flat plate with
 * jagged edges: every value above the cut collapses onto exactly 255 and all the
 * separation between them is gone. Compressing the interval [knee, peak] into
 * [knee, 1] keeps that separation, still reaches a true white at the top, and
 * costs nothing when there is no overflow to fold.
 */
float foldHighlights(float x, float peak) {
  if (peak <= 1.0001) return clamp(x, 0.0, 1.0);
  if (x <= 0.0) return 0.0;
  // The fold has to act over a wide band, not just the sliver that overflows.
  // Confining it to the overflow leaves the stretch essentially linear and the
  // brightest values still pile onto 255; spreading it across several times the
  // overflow gives the compression somewhere to go, so bright fabric keeps its
  // texture. The band closes smoothly as the stretch returns to neutral.
  float knee = clamp(1.0 - (peak - 1.0) * 4.0, 0.45, 1.0);
  if (x <= knee) return x;
  float over = clamp((x - knee) / (peak - knee), 0.0, 1.0);
  return knee + (1.0 - knee) * (1.0 - (1.0 - over) * (1.0 - over));
}
vec3 foldHighlights(vec3 c, float peak) {
  return vec3(foldHighlights(c.r, peak), foldHighlights(c.g, peak), foldHighlights(c.b, peak));
}

// Hash for grain. Deterministic per pixel, so the same recipe always renders
// the same frame — grain that shimmered between renders would break replay.
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
`;

/**
 * Geometry: orientation, straightening, flips and crop.
 * Output is exactly the cropped frame, at the cropped aspect ratio.
 */
export const GEOMETRY_FRAGMENT = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uSource;
uniform vec4 uCrop;          // x, y, width, height in source UV space
uniform float uRotate;       // straightening, radians
uniform int uOrientation;    // quarter turns
uniform vec2 uFlip;          // -1 to mirror on that axis
uniform float uSourceAspect; // width / height of the oriented source

void main() {
  vec2 uv = vUv;

  // Crop window first: everything downstream sees only the kept frame.
  uv = uCrop.xy + uv * uCrop.zw;

  // Straighten about the centre of the crop.
  vec2 centre = uCrop.xy + uCrop.zw * 0.5;
  vec2 offset = uv - centre;
  offset.x *= uSourceAspect;
  float s = sin(uRotate);
  float c = cos(uRotate);
  offset = vec2(offset.x * c - offset.y * s, offset.x * s + offset.y * c);
  offset.x /= uSourceAspect;
  uv = centre + offset;

  // Quarter turns and mirroring.
  vec2 p = uv - 0.5;
  for (int i = 0; i < 3; i++) {
    if (i < uOrientation) p = vec2(-p.y, p.x);
  }
  p *= uFlip;
  uv = p + 0.5;

  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    fragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }
  fragColor = vec4(texture(uSource, uv).rgb, 1.0);
}`;

/**
 * Base pass: white balance and exposure on linear light, a soft highlight
 * shoulder, the sRGB encode, then the four tone-region controls and contrast.
 */
export const BASE_FRAGMENT = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uSource;
uniform mat3 uWhiteBalance;
uniform float uExposure;      // stops
uniform float uContrast;      // -1..1
uniform float uHighlights;    // -1..1
uniform float uShadows;
uniform float uWhites;
uniform float uBlacks;
/** Largest value the levels stage can produce for this recipe. 1 means none. */
uniform float uPeak;

${COLOR_HELPERS}

// Asymptotic shoulder: nothing above the knee can ever reach 1.0, so specular
// highlights roll off into white instead of shearing into a flat clipped plate.
float shoulder(float x) {
  const float knee = 0.72;
  if (x <= knee) return x;
  float headroom = 1.0 - knee;
  return knee + headroom * (1.0 - exp(-(x - knee) / headroom));
}
vec3 shoulder(vec3 c) { return vec3(shoulder(c.r), shoulder(c.g), shoulder(c.b)); }

// Region gamma: brighten or darken a tonal band without moving the endpoints.
float region(float d, float amount, float weight) {
  if (abs(amount) < 1e-4 || weight < 1e-4) return d;
  float g = exp2(-amount * 1.35);
  return mix(d, pow(max(d, 1e-5), g), weight);
}

float applyContrast(float x, float a) {
  if (abs(a) < 1e-4) return x;
  const float pivot = 0.42;
  x = clamp(x, 0.0, 1.0);
  if (a > 0.0) {
    float t = x < pivot ? x / pivot : (x - pivot) / (1.0 - pivot);
    float s = t * t * (3.0 - 2.0 * t);
    float y = x < pivot ? s * pivot : pivot + s * (1.0 - pivot);
    return mix(x, y, a);
  }
  return mix(x, pivot + (x - pivot) * 0.45, -a);
}

void main() {
  vec3 c = texture(uSource, vUv).rgb;

  // --- scene-linear ---
  c = max(uWhiteBalance * c, 0.0);
  c *= exp2(uExposure);
  c = shoulder(c);

  // --- display-referred ---
  vec3 d = clamp(linearToSrgb(c), 0.0, 1.0);

  // Whites and blacks move the endpoints; this is a levels move, so it is
  // applied before the region controls that assume a settled range. Nothing is
  // clamped here — the overflow is folded back in once, at the end.
  float white = 1.0 - uWhites * 0.24;
  float black = -uBlacks * 0.16;
  d = max((d - black) / max(white - black, 0.05), 0.0);

  float y = luma(d);
  float highlightWeight = smoothstep(0.42, 1.0, min(y, 1.0));
  float shadowWeight = 1.0 - smoothstep(0.0, 0.58, y);

  float adjusted = y;
  adjusted = region(adjusted, uHighlights, highlightWeight);
  adjusted = region(adjusted, uShadows, shadowWeight);
  adjusted = applyContrast(adjusted, uContrast);

  // Re-apply as a luminance ratio so hue and saturation survive the tone move.
  float ratio = y > 1e-4 ? adjusted / y : 1.0;
  d = max(d * ratio, 0.0);

  fragColor = vec4(foldHighlights(d, uPeak), 1.0);
}`;

/** Half-resolution downsample with a 4-tap box, used to build the blur pyramid. */
export const DOWNSAMPLE_FRAGMENT = `#version 300 es
precision highp float;
precision highp sampler2D;
in vec2 vUv;
out vec4 fragColor;
uniform sampler2D uSource;
uniform vec2 uTexel;
void main() {
  vec3 c = texture(uSource, vUv + vec2(-0.5, -0.5) * uTexel).rgb
         + texture(uSource, vUv + vec2( 0.5, -0.5) * uTexel).rgb
         + texture(uSource, vUv + vec2(-0.5,  0.5) * uTexel).rgb
         + texture(uSource, vUv + vec2( 0.5,  0.5) * uTexel).rgb;
  fragColor = vec4(c * 0.25, 1.0);
}`;

/** Separable 9-tap gaussian. */
export const BLUR_FRAGMENT = `#version 300 es
precision highp float;
precision highp sampler2D;
in vec2 vUv;
out vec4 fragColor;
uniform sampler2D uSource;
uniform vec2 uDirection; // texel-sized step along one axis
void main() {
  const float w0 = 0.2270270270;
  const float w1 = 0.3162162162;
  const float w2 = 0.0702702703;
  vec3 c = texture(uSource, vUv).rgb * w0;
  c += texture(uSource, vUv + uDirection * 1.3846153846).rgb * w1;
  c += texture(uSource, vUv - uDirection * 1.3846153846).rgb * w1;
  c += texture(uSource, vUv + uDirection * 3.2307692308).rgb * w2;
  c += texture(uSource, vUv - uDirection * 3.2307692308).rgb * w2;
  fragColor = vec4(c, 1.0);
}`;

export const MAX_MASKS = 8;

/**
 * Main pass: local contrast, curves, colour mixer, grading, saturation, local
 * adjustments, vignette and grain.
 *
 * Two render targets: the graded colour, and a control channel carrying the
 * per-pixel local sharpening / noise-reduction weights the detail pass needs.
 */
export const MAIN_FRAGMENT = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 vUv;
layout(location = 0) out vec4 fragColor;
layout(location = 1) out vec4 fragControl;

uniform sampler2D uBase;
uniform sampler2D uBlurNear;   // ~1/4 scale, clarity radius
uniform sampler2D uBlurFar;    // ~1/16 scale, haze estimate
uniform sampler2D uCurveLut;   // r = master, g = red, b = green, a = blue

uniform vec2 uTexel;
uniform float uTexture;
uniform float uClarity;
uniform float uDehaze;
uniform float uVibrance;
uniform float uSaturation;

uniform float uHslHue[8];
uniform float uHslSat[8];
uniform float uHslLum[8];

uniform vec3 uGradeShadows;    // rgb tint direction * strength
uniform vec3 uGradeMidtones;
uniform vec3 uGradeHighlights;
uniform vec3 uGradeGlobal;
uniform vec3 uGradeLuminance;  // shadows, midtones, highlights offsets
uniform float uGradeBlending;
uniform float uGradeBalance;

uniform float uVignetteAmount;
uniform float uVignetteMidpoint;
uniform float uVignetteFeather;
uniform float uVignetteRoundness;
uniform float uGrainAmount;
uniform float uGrainSize;
uniform float uAspect;
uniform float uSeed;

uniform int uMaskCount;
uniform int uMaskKind[${MAX_MASKS}];
uniform int uMaskInvert[${MAX_MASKS}];
uniform float uMaskFeather[${MAX_MASKS}];
uniform float uMaskOpacity[${MAX_MASKS}];
uniform vec4 uMaskRadial[${MAX_MASKS}];   // cx, cy, rx, ry
uniform float uMaskAngle[${MAX_MASKS}];
uniform vec4 uMaskLinear[${MAX_MASKS}];   // x1, y1, x2, y2
uniform vec4 uMaskLuma[${MAX_MASKS}];     // low, high, lowFeather, highFeather
uniform vec3 uMaskColor[${MAX_MASKS}];    // hue(0..1), range(0..1), minSat
// exposure, contrast, highlights, shadows | whites, blacks, temperature, tint
uniform vec4 uMaskToneA[${MAX_MASKS}];
uniform vec4 uMaskToneB[${MAX_MASKS}];
// saturation, vibrance, clarity, texture
uniform vec4 uMaskColorAdj[${MAX_MASKS}];
// sharpness, noise
uniform vec2 uMaskDetail[${MAX_MASKS}];

uniform int uOverlayMask;      // index of the mask to visualise, -1 for none

const float HSL_CENTRES[8] = float[8](0.0, 30.0, 60.0, 120.0, 180.0, 240.0, 280.0, 320.0);
const float HSL_WIDTH = 46.0;

${COLOR_HELPERS}

float curveLookup(float x, int channel) {
  vec4 s = texture(uCurveLut, vec2(clamp(x, 0.0, 1.0), 0.5));
  return channel == 0 ? s.r : channel == 1 ? s.g : channel == 2 ? s.b : s.a;
}

float hueDistance(float a, float b) {
  float d = abs(mod(a - b + 540.0, 360.0) - 180.0);
  return d;
}

float regionGamma(float d, float amount, float weight) {
  if (abs(amount) < 1e-4 || weight < 1e-4) return d;
  float g = exp2(-amount * 1.35);
  return mix(d, pow(max(d, 1e-5), g), weight);
}

float softContrast(float x, float a) {
  if (abs(a) < 1e-4) return x;
  const float pivot = 0.42;
  x = clamp(x, 0.0, 1.0);
  if (a > 0.0) {
    float t = x < pivot ? x / pivot : (x - pivot) / (1.0 - pivot);
    float s = t * t * (3.0 - 2.0 * t);
    float y = x < pivot ? s * pivot : pivot + s * (1.0 - pivot);
    return mix(x, y, a);
  }
  return mix(x, pivot + (x - pivot) * 0.45, -a);
}

float maskCoverage(int i, vec2 uv, vec3 colour) {
  float coverage = 0.0;
  int kind = uMaskKind[i];
  float feather = max(uMaskFeather[i], 0.001);

  if (kind == 0) {
    // Radial: distance in the ellipse's own rotated frame.
    vec2 p = uv - uMaskRadial[i].xy;
    p.x *= uAspect;
    float a = uMaskAngle[i];
    p = vec2(p.x * cos(a) + p.y * sin(a), -p.x * sin(a) + p.y * cos(a));
    vec2 radii = max(uMaskRadial[i].zw * vec2(uAspect, 1.0), vec2(1e-4));
    float d = length(p / radii);
    coverage = 1.0 - smoothstep(1.0 - feather, 1.0 + feather * 0.25, d);
  } else if (kind == 1) {
    // Linear gradient along the drawn axis.
    vec2 a = uMaskLinear[i].xy;
    vec2 b = uMaskLinear[i].zw;
    vec2 axis = b - a;
    float lengthSquared = max(dot(axis, axis), 1e-6);
    float t = dot(uv - a, axis) / lengthSquared;
    coverage = smoothstep(-feather, 1.0 + feather, t);
  } else if (kind == 2) {
    // Luminance range.
    float y = luma(colour);
    float low = uMaskLuma[i].x;
    float high = uMaskLuma[i].y;
    float lowFeather = max(uMaskLuma[i].z, 1e-4);
    float highFeather = max(uMaskLuma[i].w, 1e-4);
    coverage = smoothstep(low - lowFeather, low + lowFeather, y)
             * (1.0 - smoothstep(high - highFeather, high + highFeather, y));
  } else {
    // Colour range.
    vec3 hsv = rgbToHsv(colour);
    float d = hueDistance(hsv.x * 360.0, uMaskColor[i].x * 360.0);
    float range = max(uMaskColor[i].y * 180.0, 1.0);
    coverage = (1.0 - smoothstep(range * 0.6, range, d))
             * smoothstep(uMaskColor[i].z * 0.5, uMaskColor[i].z + 0.06, hsv.y);
  }

  coverage = clamp(coverage, 0.0, 1.0);
  if (uMaskInvert[i] == 1) coverage = 1.0 - coverage;
  return coverage * uMaskOpacity[i];
}

vec3 applySaturation(vec3 c, float saturation, float vibrance) {
  if (abs(saturation) < 1e-4 && abs(vibrance) < 1e-4) return c;

  vec3 hsv = rgbToHsv(c);

  if (abs(vibrance) > 1e-4) {
    // Muted colours move first, and the skin band is deliberately held back —
    // a vibrance slider that turns faces orange is the fastest way to lose a
    // portrait photographer's trust.
    //
    // The presence term is the part that is easy to get wrong. Weighting only by
    // headroom (1 - saturation) gives the *strongest* boost to the pixels with
    // the least colour, and a near-neutral pixel has no numerically meaningful
    // hue: rgbToHsv divides by a delta that is almost zero, so adjacent
    // pixels of white fabric come back with completely unrelated hues.
    // Amplifying those turned bright fur into flat patches of pink and yellow
    // with hard edges. Vibrance must therefore fade in from zero saturation as
    // well as out towards full — colour that is not there cannot be lifted.
    float presence = smoothstep(0.02, 0.16, hsv.y);
    float headroom = 1.0 - hsv.y;
    float skin = 1.0 - 0.55 * (1.0 - smoothstep(55.0, 12.0, abs(hsv.x * 360.0 - 28.0)));
    hsv.y = clamp(hsv.y + vibrance * headroom * presence * 0.85 * skin, 0.0, 1.0);
  }
  if (abs(saturation) > 1e-4) {
    hsv.y = clamp(hsv.y * (1.0 + saturation), 0.0, 1.0);
  }
  return hsvToRgb(hsv);
}

vec3 applyHsl(vec3 c) {
  vec3 hsv = rgbToHsv(c);
  // Same reasoning as vibrance: a pixel with almost no colour has no reliable
  // hue, so it cannot be assigned to a colour band. Fade the band weights in
  // rather than cutting at a threshold, or the boundary itself becomes visible.
  float presence = smoothstep(0.015, 0.09, hsv.y);
  if (presence <= 0.0) return c;
  float hueDegrees = hsv.x * 360.0;

  float hueShift = 0.0;
  float satShift = 0.0;
  float lumShift = 0.0;
  float total = 0.0;

  for (int i = 0; i < 8; i++) {
    float w = 1.0 - smoothstep(0.0, HSL_WIDTH, hueDistance(hueDegrees, HSL_CENTRES[i]));
    if (w <= 0.0) continue;
    hueShift += uHslHue[i] * w;
    satShift += uHslSat[i] * w;
    lumShift += uHslLum[i] * w;
    total += w;
  }
  if (total < 1e-4) return c;
  float inverse = presence / max(total, 1.0);
  hueShift *= inverse;
  satShift *= inverse;
  lumShift *= inverse;

  hsv.x = fract(hsv.x + hueShift * (30.0 / 360.0));
  hsv.y = clamp(hsv.y * (1.0 + satShift), 0.0, 1.0);
  vec3 result = hsvToRgb(hsv);
  if (abs(lumShift) > 1e-4) {
    float y = luma(result);
    float target = clamp(y * (1.0 + lumShift * 0.85), 0.0, 1.0);
    result = clamp(result * (y > 1e-4 ? target / y : 1.0), 0.0, 1.0);
  }
  return result;
}

vec3 applyGrading(vec3 c) {
  float y = luma(c);
  float balance = uGradeBalance * 0.3;
  float spread = mix(0.18, 0.55, uGradeBlending);

  float shadowWeight = 1.0 - smoothstep(0.0, 0.5 + balance + spread * 0.5, y);
  float highlightWeight = smoothstep(0.5 + balance - spread * 0.5, 1.0, y);
  float midWeight = clamp(1.0 - shadowWeight - highlightWeight, 0.0, 1.0);

  vec3 tint = uGradeShadows * shadowWeight
            + uGradeMidtones * midWeight
            + uGradeHighlights * highlightWeight
            + uGradeGlobal;

  float lift = uGradeLuminance.x * shadowWeight
             + uGradeLuminance.y * midWeight
             + uGradeLuminance.z * highlightWeight;

  // Tint is added as a colour offset scaled by how much room the pixel has, so
  // a strong shadow grade cannot push the blacks past legal.
  vec3 room = mix(vec3(1.0), 1.0 - c, step(0.0, tint));
  c = clamp(c + tint * room * 0.55, 0.0, 1.0);
  c = clamp(c + lift * 0.35, 0.0, 1.0);
  return c;
}

void main() {
  vec3 base = texture(uBase, vUv).rgb;
  vec3 c = base;

  // --- local contrast --------------------------------------------------
  if (abs(uTexture) > 1e-4) {
    // 5-tap cross at one texel: fine detail only.
    vec3 neighbourhood = texture(uBase, vUv + vec2(uTexel.x, 0.0)).rgb
                       + texture(uBase, vUv - vec2(uTexel.x, 0.0)).rgb
                       + texture(uBase, vUv + vec2(0.0, uTexel.y)).rgb
                       + texture(uBase, vUv - vec2(0.0, uTexel.y)).rgb;
    vec3 small = neighbourhood * 0.25;
    float detail = luma(c) - luma(small);
    // Roll the effect off at both ends so texture never manufactures clipping.
    float room = 1.0 - abs(luma(c) * 2.0 - 1.0);
    c = clamp(c + detail * uTexture * 2.2 * room, 0.0, 1.0);
  }

  if (abs(uClarity) > 1e-4) {
    float wide = luma(texture(uBlurNear, vUv).rgb);
    float y = luma(c);
    float detail = y - wide;
    // Mid-tone weighted: clarity that bites into the shadows just looks muddy.
    float weight = 1.0 - abs(y * 2.0 - 1.0);
    float target = clamp(y + detail * uClarity * 1.5 * weight, 0.0, 1.0);
    c = clamp(c * (y > 1e-4 ? target / y : 1.0), 0.0, 1.0);
  }

  if (abs(uDehaze) > 1e-4) {
    // Haze reads as a low-frequency lift of the darkest channel. Estimating
    // that veil and re-expanding around it recovers contrast without the
    // global black-crush a contrast slider would cause.
    vec3 far = texture(uBlurFar, vUv).rgb;
    float veil = min(min(far.r, far.g), far.b);
    float strength = uDehaze * 0.75;
    vec3 dehazed = (c - veil * strength) / max(1.0 - veil * strength, 0.25);
    c = clamp(mix(c, dehazed, clamp(abs(uDehaze), 0.0, 1.0)), 0.0, 1.0);
    if (uDehaze > 0.0) c = applySaturation(c, uDehaze * 0.18, 0.0);
  }

  // --- curves -----------------------------------------------------------
  float master = curveLookup(c.r, 0);
  c.r = curveLookup(master, 1);
  master = curveLookup(c.g, 0);
  c.g = curveLookup(master, 2);
  master = curveLookup(c.b, 0);
  c.b = curveLookup(master, 3);

  // --- colour -----------------------------------------------------------
  c = applyHsl(c);
  c = applyGrading(c);
  c = applySaturation(c, uSaturation, uVibrance);

  // --- local adjustments -------------------------------------------------
  float localSharpen = 0.0;
  float localNoise = 0.0;
  float overlay = 0.0;

  for (int i = 0; i < ${MAX_MASKS}; i++) {
    if (i >= uMaskCount) break;
    float m = maskCoverage(i, vUv, c);
    if (m <= 0.001) continue;
    if (uOverlayMask == i) overlay = m;

    vec4 toneA = uMaskToneA[i];  // exposure, contrast, highlights, shadows
    vec4 toneB = uMaskToneB[i];  // whites, blacks, temperature, tint
    vec4 colourAdj = uMaskColorAdj[i];

    vec3 local = c;
    if (abs(toneA.x) > 1e-4) local = clamp(local * exp2(toneA.x), 0.0, 1.0);
    if (abs(toneB.x) > 1e-4 || abs(toneB.y) > 1e-4) {
      float white = 1.0 - toneB.x * 0.24;
      float black = -toneB.y * 0.16;
      float peak = (1.0 - black) / max(white - black, 0.05);
      local = foldHighlights(max((local - black) / max(white - black, 0.05), 0.0), peak);
    }

    float y = luma(local);
    float adjusted = y;
    adjusted = regionGamma(adjusted, toneA.z, smoothstep(0.42, 1.0, y));
    adjusted = regionGamma(adjusted, toneA.w, 1.0 - smoothstep(0.0, 0.58, y));
    adjusted = softContrast(adjusted, toneA.y);
    local = clamp(local * (y > 1e-4 ? adjusted / y : 1.0), 0.0, 1.0);

    // Local temperature and tint act as simple opponent shifts: a full
    // colorimetric re-balance inside a mask would fight the global one.
    if (abs(toneB.z) > 1e-4 || abs(toneB.w) > 1e-4) {
      local.r = clamp(local.r + toneB.z * 0.12, 0.0, 1.0);
      local.b = clamp(local.b - toneB.z * 0.12, 0.0, 1.0);
      local.g = clamp(local.g - toneB.w * 0.10, 0.0, 1.0);
    }

    if (abs(colourAdj.x) > 1e-4 || abs(colourAdj.y) > 1e-4) {
      local = applySaturation(local, colourAdj.x, colourAdj.y);
    }
    if (abs(colourAdj.z) > 1e-4) {
      float wide = luma(texture(uBlurNear, vUv).rgb);
      float yl = luma(local);
      float target = clamp(yl + (yl - wide) * colourAdj.z * 1.5, 0.0, 1.0);
      local = clamp(local * (yl > 1e-4 ? target / yl : 1.0), 0.0, 1.0);
    }
    if (abs(colourAdj.w) > 1e-4) {
      vec3 small = (texture(uBase, vUv + vec2(uTexel.x, 0.0)).rgb
                  + texture(uBase, vUv - vec2(uTexel.x, 0.0)).rgb
                  + texture(uBase, vUv + vec2(0.0, uTexel.y)).rgb
                  + texture(uBase, vUv - vec2(0.0, uTexel.y)).rgb) * 0.25;
      local = clamp(local + (luma(local) - luma(small)) * colourAdj.w * 2.0, 0.0, 1.0);
    }

    c = mix(c, local, m);
    localSharpen += uMaskDetail[i].x * m;
    localNoise += uMaskDetail[i].y * m;
  }

  // --- effects -----------------------------------------------------------
  if (abs(uVignetteAmount) > 1e-4) {
    vec2 p = (vUv - 0.5) * 2.0;
    p.x *= mix(uAspect, 1.0, clamp(uVignetteRoundness * 0.5 + 0.5, 0.0, 1.0));
    float d = length(p) / max(uVignetteMidpoint * 2.0, 0.05);
    float feather = max(uVignetteFeather, 0.02);
    float v = smoothstep(1.0 - feather, 1.0 + feather, d);
    if (uVignetteAmount < 0.0) {
      c *= 1.0 + uVignetteAmount * v;
    } else {
      c = mix(c, vec3(1.0), uVignetteAmount * v * 0.55);
    }
    c = clamp(c, 0.0, 1.0);
  }

  if (uGrainAmount > 1e-4) {
    float scale = mix(1.0, 4.0, uGrainSize);
    vec2 grainUv = floor(vUv / (uTexel * scale)) * uTexel * scale;
    float n = hash21(grainUv + uSeed) - 0.5;
    // Grain is strongest in the mid-tones, as it is on film.
    float y = luma(c);
    float weight = 1.0 - abs(y * 2.0 - 1.0);
    c = clamp(c + n * uGrainAmount * 0.28 * weight, 0.0, 1.0);
  }

  fragColor = vec4(c, 1.0);
  fragControl = vec4(clamp(localSharpen, -1.0, 1.0), clamp(localNoise, 0.0, 1.0), overlay, 1.0);
}`;

/**
 * Detail pass: noise reduction then sharpening, both edge-aware, both able to
 * be steered per-pixel by a local adjustment.
 */
export const DETAIL_FRAGMENT = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uSource;
uniform sampler2D uControl;
uniform vec2 uTexel;

uniform float uSharpenAmount;
uniform float uSharpenRadius;
uniform float uSharpenDetail;
uniform float uSharpenMasking;
uniform float uNoiseLuminance;
uniform float uNoiseDetail;
uniform float uNoiseColor;
uniform float uShowClipping;
uniform float uOverlayStrength;

${COLOR_HELPERS}

void main() {
  vec4 control = texture(uControl, vUv);
  vec3 c = texture(uSource, vUv).rgb;

  float noiseAmount = clamp(uNoiseLuminance + control.g, 0.0, 1.0);
  float sharpenAmount = max(uSharpenAmount + control.r * 1.5, 0.0);

  // --- noise reduction ---------------------------------------------------
  if (noiseAmount > 1e-3 || uNoiseColor > 1e-3) {
    vec3 sum = vec3(0.0);
    float weightSum = 0.0;
    float centreLuma = luma(c);
    // Edge-aware 3x3: weight neighbours by how close they are in luminance, so
    // flat areas smooth and edges survive.
    float sigma = mix(0.09, 0.012, uNoiseDetail);
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 offset = vec2(float(x), float(y)) * uTexel * 1.5;
        vec3 s = texture(uSource, vUv + offset).rgb;
        float d = abs(luma(s) - centreLuma);
        float w = exp(-(d * d) / (2.0 * sigma * sigma));
        sum += s * w;
        weightSum += w;
      }
    }
    vec3 smoothed = sum / max(weightSum, 1e-4);

    if (noiseAmount > 1e-3) {
      float y = luma(c);
      float ys = luma(smoothed);
      c = clamp(c * (y > 1e-4 ? mix(y, ys, noiseAmount) / y : 1.0), 0.0, 1.0);
    }
    if (uNoiseColor > 1e-3) {
      // Chroma blurs much harder than luma: colour blotches are large and
      // carry no detail worth keeping.
      float y = luma(c);
      vec3 chroma = c - vec3(y);
      float ys = luma(smoothed);
      vec3 chromaSmooth = smoothed - vec3(ys);
      c = clamp(vec3(y) + mix(chroma, chromaSmooth, uNoiseColor), 0.0, 1.0);
    }
  }

  // --- sharpening --------------------------------------------------------
  if (sharpenAmount > 1e-3) {
    vec2 step = uTexel * uSharpenRadius;
    vec3 blur = texture(uSource, vUv + vec2(step.x, 0.0)).rgb
              + texture(uSource, vUv - vec2(step.x, 0.0)).rgb
              + texture(uSource, vUv + vec2(0.0, step.y)).rgb
              + texture(uSource, vUv - vec2(0.0, step.y)).rgb
              + texture(uSource, vUv + step).rgb
              + texture(uSource, vUv - step).rgb
              + texture(uSource, vUv + vec2(step.x, -step.y)).rgb
              + texture(uSource, vUv + vec2(-step.x, step.y)).rgb;
    blur /= 8.0;

    float y = luma(c);
    float high = y - luma(blur);

    // Edge masking: skin and sky keep their smoothness, edges get the acutance.
    float edge = length(vec2(
      luma(texture(uSource, vUv + vec2(uTexel.x, 0.0)).rgb) - luma(texture(uSource, vUv - vec2(uTexel.x, 0.0)).rgb),
      luma(texture(uSource, vUv + vec2(0.0, uTexel.y)).rgb) - luma(texture(uSource, vUv - vec2(0.0, uTexel.y)).rgb)
    ));
    float mask = uSharpenMasking < 1e-3 ? 1.0 : smoothstep(0.0, mix(0.10, 0.005, 1.0 - uSharpenMasking), edge);

    // Detail controls how much of the fine signal survives the threshold.
    float threshold = mix(0.02, 0.0, uSharpenDetail);
    float signal = sign(high) * max(abs(high) - threshold, 0.0);

    float target = clamp(y + signal * sharpenAmount * mask, 0.0, 1.0);
    c = clamp(c * (y > 1e-4 ? target / y : 1.0), 0.0, 1.0);
  }

  // Dither by less than one code value before the frame is quantised to 8 bits.
  // A smooth gradient across a wall or a sky otherwise lands on visible steps;
  // this trades them for noise far below what the eye resolves.
  float dither = (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
  c = clamp(c + dither, 0.0, 1.0);

  // --- inspection overlays -----------------------------------------------
  if (uShowClipping > 0.5) {
    if (c.r >= 0.996 && c.g >= 0.996 && c.b >= 0.996) c = vec3(1.0, 0.15, 0.05);
    else if (c.r <= 0.004 && c.g <= 0.004 && c.b <= 0.004) c = vec3(0.15, 0.45, 1.0);
  }
  if (uOverlayStrength > 0.0 && control.b > 0.001) {
    c = mix(c, vec3(0.86, 0.31, 0.0), control.b * uOverlayStrength * 0.45);
  }

  fragColor = vec4(c, 1.0);
}`;

/** Present: view transform onto the canvas, with an optional before/after wipe. */
export const PRESENT_FRAGMENT = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uProcessed;
uniform sampler2D uOriginal;
uniform vec2 uScale;
uniform vec2 uOffset;
uniform float uSplit;        // 0..1 wipe position, <0 disables
uniform float uSplitEnabled;
uniform vec3 uBackground;

void main() {
  // Every intermediate target holds row 0 of the source at its *bottom*, because
  // the source texture is uploaded top-down while clip space runs bottom-up.
  // The chain is self-consistent, so the flip is paid exactly once — here, at
  // the boundary where pixels become something a person looks at.
  vec2 view = vec2(vUv.x, 1.0 - vUv.y);
  vec2 uv = (view - uOffset) / uScale;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    fragColor = vec4(uBackground, 1.0);
    return;
  }

  vec3 processed = texture(uProcessed, uv).rgb;
  if (uSplitEnabled > 0.5) {
    vec3 original = texture(uOriginal, uv).rgb;
    float edge = smoothstep(uSplit - 0.0012, uSplit + 0.0012, vUv.x);
    vec3 mixed = mix(original, processed, edge);
    float line = 1.0 - smoothstep(0.0009, 0.0018, abs(vUv.x - uSplit));
    fragColor = vec4(mix(mixed, vec3(0.86, 0.31, 0.0), line), 1.0);
    return;
  }
  fragColor = vec4(processed, 1.0);
}`;
